import { CartLine } from '@lambda/commons/apis/shopify/types/storefront';
import { Dispatch, SetStateAction, useState } from 'react';
import { RC_DURATION, TagHelpers } from '@lambda/reebelo';
import Image from 'next/future/image';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import Loading from '@/components/Loading/Loading';
import useCart, {
  CartItemAttribute,
  encodeDecodeForAttr,
  generateAddCartDateTimeAttr,
  generateIDAttr,
} from '@/lib/use-cart';
import shieldIcon from '@/public/icons/buyback/sell-device/shield.svg';
import { logger } from '@/lib/logger';
import WarrantyDisclaimer from '@/components/collections/[handle]/WarrantyDisclaimer';
import {
  generateWarrantyUpsellContent,
  generateWarrantyUpsellCta,
  generateWarrantyUpsellTitle,
} from '@/lib/reebelocare/helpers';
import { ProductWarrantyT } from '@/lib/reebelocare/types';
import AdditionalSingleDevice from './AdditionalSingleDevice';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';
import { trackReebeloCareAdded } from '@/lib/analytics/trackProduct';

const SingleDevice = ({
  onContinue,
  device,
  isCheckingOut,
  setIsCheckingOut,
  productWarranty,
}: {
  onContinue: () => void;
  device: CartLine;
  isCheckingOut: boolean;
  setIsCheckingOut: Dispatch<SetStateAction<boolean>>;
  productWarranty: ProductWarrantyT;
}) => {
  const cart = useCart();
  const cartId = cart?.cart?.id;

  const isAdditionalMonthsEnabled = Object.keys(productWarranty).length > 1;

  const [duration, setDuration] = useState<RC_DURATION>(
    RC_DURATION['24_MONTH'],
  );

  const {
    sku,
    image: { url },
    product: { vendor, title },
  } = device.merchandise;

  const { category, psku } = {
    ...TagHelpers.extractTags(device.merchandise.product.tags),
    ...device.merchandise,
  };

  const trackingProps = (_duration: RC_DURATION) => {
    const _trackingProps: ReebeloCareTrackRequest = {
      entry_point: 'Cart Pop-up',
      reebelocare_id: (productWarranty[_duration] as WarrantyT)
        .shopifyVariantId,
      device_psku: psku ?? '',
      device_vendor: vendor ?? '',
      device_sku: sku ?? '',
      duration: _duration,
    };

    return _trackingProps;
  };

  const attributes: CartItemAttribute[] = [
    ...encodeDecodeForAttr.encode({
      sku,
      title,
    }),
    ...generateIDAttr(vendor, sku),
    ...generateAddCartDateTimeAttr(),
    { key: 'Duration', value: duration },
    {
      key: '_offer_category',
      value: category ?? '',
    },
  ];

  const onRcContinue = async () => {
    setIsCheckingOut(true);

    try {
      if (productWarranty[duration]) {
        await cart
          .add(
            {
              merchandiseId: `gid://shopify/ProductVariant/${
                (productWarranty[duration] as WarrantyT).shopifyVariantId
              }`,
              attributes,
              quantity: device.quantity,
            },
            cartId,
          )
          .then(() => {
            trackReebeloCareAdded(trackingProps(duration));

            onContinue();
          });
      }
    } catch (error) {
      logger.error({ error }, 'Error Adding Upsell ReebeloCare');
    }
  };

  if (!productWarranty) return null;

  if (isAdditionalMonthsEnabled) {
    return (
      <AdditionalSingleDevice
        url={url}
        title={title}
        productWarranty={productWarranty}
        isCheckingOut={isCheckingOut}
        onContinue={onContinue}
        setIsCheckingOut={setIsCheckingOut}
        onRcContinue={onRcContinue}
        setChecked={setDuration}
        checked={duration}
        trackingProps={trackingProps}
      />
    );
  }

  return (
    <section className="p-5 sm:p-10">
      <div>
        <div className="relative mx-auto h-[115px] w-[115px] rounded-xl bg-[#ECFCF5] p-4">
          <div className="h-full w-full">
            <Image
              src={url}
              alt={title}
              height={300}
              width={300}
              priority
              loading="eager"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="absolute -top-3 -right-6 h-12 w-12">
            <Image
              src={shieldIcon}
              height={50}
              width={50}
              alt="ReebeloCare"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
      <div className="mt-5 text-center">
        <h3 className="text-base font-bold">{generateWarrantyUpsellTitle()}</h3>
        <p className="mt-2 text-sm leading-tight text-gray-700/70">
          {generateWarrantyUpsellContent(
            productWarranty[RC_DURATION['24_MONTH']].price,
            productWarranty[RC_DURATION['24_MONTH']].duration,
          )}
        </p>
      </div>
      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <button
          onClick={() => {
            setIsCheckingOut(true);
            onContinue();
          }}
          type="button"
          className="flex h-10 w-full items-center justify-center rounded-lg border border-gray-700 bg-white text-xs font-bold text-gray-700 underline-offset-2 outline-none hover:underline"
        >
          {generateWarrantyUpsellCta().reject}
        </button>
        <button
          disabled={isCheckingOut}
          type="button"
          className="flex h-10 w-full items-center justify-center rounded-lg border border-gray-700 bg-gray-700 text-xs font-bold text-white underline-offset-2 outline-none hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => onRcContinue()}
        >
          {isCheckingOut ? (
            <Loading size="xs" />
          ) : (
            generateWarrantyUpsellCta().accept
          )}
        </button>
      </div>
      <WarrantyDisclaimer className="mt-2.5" />
    </section>
  );
};

export default SingleDevice;
